import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Lock, Eye, Database, UserCheck, Mail } from 'lucide-react';

export default function Privacy() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 text-white">
      {/* Header */}
      <header className="border-b border-slate-700/50 bg-slate-900/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span>Volver al inicio</span>
          </Link>
          <div className="flex items-center gap-2">
            <Shield className="w-6 h-6 text-blue-400" />
            <span className="font-semibold">AI Med Planner</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-500/20 mb-6">
            <Lock className="w-8 h-8 text-blue-400" />
          </div>
          <h1 className="text-4xl font-bold mb-4">Política de Privacidad</h1>
          <p className="text-slate-400">Última actualización: Enero 2026</p>
        </div>

        <div className="space-y-8 text-slate-300">
          {/* Introduction */}
          <section className="bg-slate-800/50 rounded-2xl p-8 border border-slate-700/50">
            <h2 className="text-2xl font-semibold text-white mb-4 flex items-center gap-3">
              <Shield className="w-6 h-6 text-blue-400" />
              Introducción
            </h2>
            <p className="leading-relaxed">
              AI Med Planner ("nosotros", "nuestro" o "la plataforma") se compromete a proteger la privacidad 
              de nuestros usuarios. Esta Política de Privacidad describe cómo recopilamos, usamos, almacenamos 
              y protegemos su información personal cuando utiliza nuestros servicios de gestión de citas médicas 
              y comunicación con pacientes a través de WhatsApp.
            </p>
          </section>

          {/* Data Collection */}
          <section className="bg-slate-800/50 rounded-2xl p-8 border border-slate-700/50">
            <h2 className="text-2xl font-semibold text-white mb-4 flex items-center gap-3">
              <Database className="w-6 h-6 text-green-400" />
              Información que Recopilamos
            </h2>
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-white mb-2">Información de Clínicas y Profesionales:</h3>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Nombre de la clínica y datos de contacto</li>
                  <li>Información del médico o profesional de salud</li>
                  <li>Horarios de atención y servicios ofrecidos</li>
                  <li>Credenciales de acceso (encriptadas)</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-white mb-2">Información de Pacientes:</h3>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Nombre y número de teléfono (proporcionado vía WhatsApp)</li>
                  <li>Correo electrónico (cuando se proporciona voluntariamente)</li>
                  <li>Historial de conversaciones con el asistente de IA</li>
                  <li>Información de citas programadas</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Data Usage */}
          <section className="bg-slate-800/50 rounded-2xl p-8 border border-slate-700/50">
            <h2 className="text-2xl font-semibold text-white mb-4 flex items-center gap-3">
              <Eye className="w-6 h-6 text-purple-400" />
              Uso de la Información
            </h2>
            <p className="mb-4">Utilizamos la información recopilada para:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Facilitar la comunicación entre clínicas y pacientes</li>
              <li>Programar y gestionar citas médicas</li>
              <li>Proporcionar respuestas automatizadas mediante inteligencia artificial</li>
              <li>Enviar recordatorios y confirmaciones de citas</li>
              <li>Mejorar nuestros servicios y experiencia de usuario</li>
              <li>Cumplir con obligaciones legales y regulatorias</li>
            </ul>
          </section>

          {/* WhatsApp */}
          <section className="bg-slate-800/50 rounded-2xl p-8 border border-slate-700/50">
            <h2 className="text-2xl font-semibold text-white mb-4 flex items-center gap-3">
              <svg className="w-6 h-6 text-green-500" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              Integración con WhatsApp
            </h2>
            <p className="leading-relaxed mb-4">
              Nuestra plataforma utiliza la API de WhatsApp Business para facilitar la comunicación. 
              Al interactuar con nosotros a través de WhatsApp, usted acepta:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>El procesamiento de sus mensajes por nuestro sistema de IA</li>
              <li>El almacenamiento seguro del historial de conversaciones</li>
              <li>La recepción de mensajes relacionados con sus citas médicas</li>
            </ul>
            <p className="mt-4 text-sm text-slate-400">
              Nota: WhatsApp tiene su propia política de privacidad que puede consultar en{' '}
              <a href="https://www.whatsapp.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">
                whatsapp.com/legal/privacy-policy
              </a>
            </p>
          </section>

          {/* Data Protection */}
          <section className="bg-slate-800/50 rounded-2xl p-8 border border-slate-700/50">
            <h2 className="text-2xl font-semibold text-white mb-4 flex items-center gap-3">
              <Lock className="w-6 h-6 text-yellow-400" />
              Protección de Datos
            </h2>
            <p className="leading-relaxed mb-4">
              Implementamos medidas de seguridad técnicas y organizativas para proteger su información:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Encriptación de datos en tránsito y en reposo</li>
              <li>Acceso restringido basado en roles</li>
              <li>Monitoreo continuo de seguridad</li>
              <li>Cumplimiento con la Ley Federal de Protección de Datos Personales (LFPDPPP)</li>
              <li>Servidores seguros con certificación de seguridad</li>
            </ul>
          </section>

          {/* User Rights */}
          <section className="bg-slate-800/50 rounded-2xl p-8 border border-slate-700/50">
            <h2 className="text-2xl font-semibold text-white mb-4 flex items-center gap-3">
              <UserCheck className="w-6 h-6 text-cyan-400" />
              Sus Derechos ARCO
            </h2>
            <p className="leading-relaxed mb-4">
              De acuerdo con la legislación mexicana, usted tiene derecho a:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>Acceso:</strong> Conocer qué datos personales tenemos sobre usted</li>
              <li><strong>Rectificación:</strong> Solicitar la corrección de datos inexactos</li>
              <li><strong>Cancelación:</strong> Pedir la eliminación de sus datos</li>
              <li><strong>Oposición:</strong> Oponerse al tratamiento de sus datos</li>
            </ul>
            <p className="mt-4">
              Para ejercer estos derechos, contáctenos a través de los medios indicados abajo.
            </p>
          </section>

          {/* Contact */}
          <section className="bg-slate-800/50 rounded-2xl p-8 border border-slate-700/50">
            <h2 className="text-2xl font-semibold text-white mb-4 flex items-center gap-3">
              <Mail className="w-6 h-6 text-red-400" />
              Contacto
            </h2>
            <p className="leading-relaxed mb-4">
              Para cualquier consulta sobre esta política de privacidad o el tratamiento de sus datos:
            </p>
            <div className="bg-slate-700/50 rounded-lg p-4">
              <p><strong>AI Med Planner</strong></p>
              <p>Email: privacidad@aimedplanner.com</p>
              <p>Teléfono: +52 55 4965 2617</p>
            </div>
          </section>

          {/* Updates */}
          <section className="bg-slate-800/50 rounded-2xl p-8 border border-slate-700/50">
            <h2 className="text-2xl font-semibold text-white mb-4">Actualizaciones</h2>
            <p className="leading-relaxed">
              Nos reservamos el derecho de actualizar esta política de privacidad en cualquier momento. 
              Las modificaciones entrarán en vigor inmediatamente después de su publicación en esta página. 
              Le recomendamos revisar periódicamente esta política para mantenerse informado sobre cómo 
              protegemos su información.
            </p>
          </section>
        </div>

        {/* Footer CTA */}
        <div className="mt-12 text-center">
          <Link 
            to="/"
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-full font-semibold transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Volver al inicio
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-700/50 mt-12 py-8">
        <div className="container mx-auto px-4 text-center text-slate-400">
          <p>© 2026 AI Med Planner. Todos los derechos reservados.</p>
        </div>
      </footer>
    </div>
  );
}
